import React from "react";
import { useState } from "react";
import { Form, Button } from "react-bootstrap";
import _ from "lodash";
import JiraTicket from "../JiraTicket";
import { Spinner } from "react-bootstrap";

const MAX_ERRORS_SHOWN = 10;

export default function ImportEffectiveDatesForm(props) {
  const [networkOptions, setNetworkOptions] = useState([]);
  const [manufacturer, setManufacturer] = useState(null);
  const [network, setNetwork] = useState("");
  const [inputFile, setInputFile] = useState(null);
  const [fileValidated, setFileValidated] = useState(false);
  const [jiraTicket, setJiraTicket] = useState("");
  const [errorMessages, setErrorMessages] = useState([]);
  const [errorCount, setErrorCount] = useState(0);
  const [fileResults, setFileResults] = useState({});
  const [isBusy, setIsBusy] = useState(false);
  const [busyAction, setBusyAction] = useState(null);

  const manufacturerName = props.manufacturers.find((mfr) => mfr.key === manufacturer)?.name;

  function fileIsXLSX(selectedFile) {
    if (selectedFile && selectedFile.files.length) {
      const extName = selectedFile.files[0].name.split(".").pop().toLowerCase();
      if (extName != "xlsx") {
        alert("File must have extension .xlsx");
        selectedFile.value = null;
        setInputFile(null);
        return;
      }
      setInputFile(selectedFile.files[0]);
    } else {
      setInputFile(null);
    }
  }

  function resetValidation() {
    setErrorCount(0);
    setErrorMessages([]);
    setFileValidated(false);
    setFileResults({});
  }

  function validateDisabled() {
    return !(manufacturer && network && inputFile && !isBusy);
  }

  function submitDisabled() {
    return !(
      fileValidated &&
      fileResults.updated_effective_dates_count > 0 &&
      jiraTicket !== "" &&
      !isBusy
    );
  }

  const renderErrors = () =>
    _.map(errorMessages.slice(0, MAX_ERRORS_SHOWN), (errorMessage, index) => (
      <li key={`error${index}`}>
        <i className="fa fa-circle-xmark" />
        <span className="p-3">{errorMessage}</span>
      </li>
    ));

  const renderStatusMessage = () => {
    return fileResults ? (
      <>
        <span>File contained {fileResults.row_count} rows: </span>
        <span className="text-warning">
          {fileResults.updated_effective_dates_count} effective date(s) would be updated,{" "}
        </span>
        <span className="text-muted">
          {fileResults.unchanged_effective_dates_count} effective date(s) already match and would be
          unchanged
        </span>
        <ul className="mt-2" style={{ maxHeight: "200px", overflowY: "auto" }}>
          {_.map(fileResults.updated_effective_dates, (updated_effective_date, index) => (
            <li className="text-warning" key={`updated-${index}`}>
              <i className="fa fa-arrows-left-right pe-2" />
              {updated_effective_date}
            </li>
          ))}
        </ul>
      </>
    ) : null;
  };

  async function handleVerify(event) {
    event.preventDefault();

    if (!inputFile || isBusy) return;

    setIsBusy(true);
    setBusyAction("verify");

    const url = props.validate_url;
    const formData = new FormData();
    formData.append("file", inputFile);
    formData.append("manufacturer", manufacturer);
    formData.append("network", network);
    try {
      const response = await fetch(url, {
        method: "POST",
        headers: {
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]').getAttribute("content"),
        },
        body: formData,
      });
      const data = await response.json();
      if (!response.ok) {
        setErrorCount(data.error_count);
        setErrorMessages(data.message);
        setFileValidated(false);
        setFileResults({});
      } else {
        setErrorCount(0);
        setErrorMessages([]);
        setFileValidated(true);
        setFileResults(data);
      }
      setBusyAction(null);
      setIsBusy(false);
    } catch (error) {
      setErrorCount(1);
      setErrorMessages(["Unknown error encountered"]);
      setFileValidated(false);
      setFileResults({});

      setBusyAction(null);
      setIsBusy(false);
    }
  }

  async function handleSubmit(event) {
    event.preventDefault();

    if (!inputFile || isBusy) return;

    setIsBusy(true);
    setBusyAction("submit");

    const url = props.upload_url;
    const formData = new FormData();
    formData.append("file", inputFile);
    formData.append("manufacturer", manufacturer);
    formData.append("network", network);
    formData.append("jira_ticket", jiraTicket);

    try {
      const response = await fetch(url, {
        method: "POST",
        headers: {
          "X-CSRF-Token":
            document.querySelector('meta[name="csrf-token"]')?.getAttribute("content") ?? "",
        },
        body: formData,
      });

      if (response.ok) {
        location.reload();
        return;
      }
    } catch (err) {}
    setBusyAction(null);
    setIsBusy(false);
  }

  function handleFileChange(event) {
    resetValidation();
    fileIsXLSX(event.target);
  }

  return (
    <React.Fragment>
      <Form onSubmit={handleSubmit} id="effectiveDateUploadForm">
        <Form.Group className="mb-3" controlId="manufacturer">
          <Form.Label>
            Manufacturer <small className="text-danger">*</small>
          </Form.Label>
          <Form.Select
            required
            name="manufacturer"
            onChange={(e) => {
              setManufacturer(e.target.value);
              const mfrNetwork = props.manufacturer_networks.find(
                (mn) => mn.manufacturer === e.target.value
              );
              setNetworkOptions(mfrNetwork?.networks || []);
              setNetwork("");
              resetValidation();
            }}
          >
            <option value="">{"< Make a Selection >"}</option>
            {props.manufacturers.map((manufacturer) => (
              <option key={manufacturer.key} value={manufacturer.key}>
                {manufacturer.name}
              </option>
            ))}
          </Form.Select>
        </Form.Group>
        <Form.Group className="mb-3" controlId="network">
          <Form.Label>
            Network <small className="text-danger">*</small>
          </Form.Label>
          <Form.Select
            required
            name="network"
            value={network}
            onChange={(e) => {
              setNetwork(e.target.value);
              resetValidation();
            }}
          >
            <option value="">{"< Make a Selection >"}</option>
            {networkOptions.map((network) => (
              <option key={network} value={network}>
                {network}
              </option>
            ))}
          </Form.Select>
        </Form.Group>
        <JiraTicket setJiraTicket={setJiraTicket} />
        <Form.Group
          controlId="headers"
          className="mb-3 p-3 bg-body-tertiary rounded border shadow-sm"
        >
          <Form.Label className="text-muted smaller">
            Required Effective Date File Headers:
          </Form.Label>
          <br />
          <b className="text-primary">
            340B ID or HRSA_ID
            <br />
            Pharmacy ID (PID) or HRSA_PHARMACY_ID
            <br />
            Effective Date or EFFECTIVE_DATE
          </b>
          <br />
          <Form.Label className="text-muted smaller">Required Effective Date File Type:</Form.Label>
          <br />
          <b className="text-primary">.xlsx (Excel)</b>
          <br />
          <Form.Label className="text-muted smaller">Effective Date Format:</Form.Label>
          <br />
          <b className="text-primary">Excel date, or text formatted YYYY-MM-DD or MM/DD/YYYY</b>
          <br />
          <Form.Label className="text-muted smaller">File To Upload:</Form.Label>
          <p className="smaller">
            <span>
              Each row on the first sheet updates the date of one existing effective date for the
              selected manufacturer and network, matched on 340B ID and PID. No effective dates are
              created or removed.{" "}
            </span>
            <span className="text-danger">
              If any row has errors, <b>no</b> effective dates will be updated.
            </span>
          </p>
          <Form.Control
            required
            name="file"
            type="file"
            accept=".xlsx"
            onChange={handleFileChange}
          />
        </Form.Group>

        {errorMessages.length > 0 ? (
          <>
            <p className="text-danger">
              {errorCount} row(s) had errors (first {MAX_ERRORS_SHOWN} are shown):
              <br />
            </p>
            <ul className="text-danger" role="list">
              {renderErrors()}
            </ul>
          </>
        ) : null}
        {fileValidated ? <div className="mb-3">File validated: {renderStatusMessage()}</div> : null}
        <div className="modal__button__container d-flex align-items-center justify-content-end">
          {manufacturerName ? (
            <span className="fw-bold text-danger text-end me-3">
              {manufacturerName.toUpperCase()}
              {network && ` • ${network.toUpperCase()}`}
            </span>
          ) : null}
          <Button onClick={handleVerify} disabled={validateDisabled()}>
            {busyAction === "verify" ? (
              <>
                <Spinner
                  animation="border"
                  variant="light"
                  className="text-muted"
                  as="span"
                  size="sm"
                />
                Checking...
              </>
            ) : (
              "Check Effective Date File"
            )}
          </Button>

          <Button
            type="submit"
            variant="success"
            style={{ marginLeft: "8px" }}
            disabled={submitDisabled()}
          >
            {busyAction === "submit" ? (
              <>
                <Spinner
                  animation="border"
                  variant="light"
                  className="text-muted"
                  as="span"
                  size="sm"
                />
                Submitting...
              </>
            ) : (
              "Submit Effective Date File"
            )}
          </Button>
        </div>
      </Form>
    </React.Fragment>
  );
}
